import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { api } from '../../services/api';
import branding from '../../config/branding';
import { faqs } from '../../data/faqs';

import {
  Badge,
  Card,
  CardLabel,
  CardLink,
  CardsGrid,
  CardText,
  CardTitle,
  Container,
  CTAPrimary,
  CTARow,
  CTASecondary,
  ErrorText,
  FAQAnswer,
  FAQItem,
  FAQList,
  FAQQuestion,
  Field,
  FieldsGrid,
  Form,
  FormFooter,
  FooterNote,
  Lead,
  Panel,
  Section,
  SectionSubtitle,
  SectionTitle,
  Step,
  StepNumber,
  StepsGrid,
  StepText,
  StepTitle,
  SubmitButton,
  SuccessBox,
  Title,
  Track,
  TrackTitle,
  WarningCallout,
  WarningText,
  WarningTitle,
} from './styles';

const Support: React.FC = () => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    setSending(true);
    setError(false);

    try {
      await api.post('support-tickets', {
        full_name: formData.get('full_name'),
        email: formData.get('email'),
        request_type: formData.get('request_type'),
        device_info: formData.get('device_info') || undefined,
        message: formData.get('message'),
      });

      setSent(true);
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Header />
      <Container>
        <Badge>
          <span />
          Central de suporte · App TrenzoX
        </Badge>
        <Title>Como podemos ajudar você?</Title>
        <Lead>
          Orientações de uso, solução de problemas e canais oficiais de
          atendimento para alunos, instrutores e gestores que usam o aplicativo
          TrenzoX.
        </Lead>
        <CTARow>
          <CTAPrimary href="#contato">Falar com o suporte</CTAPrimary>
          <CTASecondary href="#duvidas">Ver dúvidas frequentes</CTASecondary>
        </CTARow>

        <CardsGrid>
          <Card highlight>
            <CardLabel highlight>Canal principal</CardLabel>
            <CardTitle>Suporte por e-mail</CardTitle>
            <CardLink href={`mailto:${branding.supportEmail}`}>
              {branding.supportEmail}
            </CardLink>
            <CardText>
              Resposta em até 1 dia útil. Atendimento de segunda a sexta, das 9h
              às 18h (horário de Brasília).
            </CardText>
          </Card>
          <Card>
            <CardLabel>Alunos</CardLabel>
            <CardTitle>Fale com sua academia</CardTitle>
            <CardText>
              Dúvidas sobre treinos, fichas, mensalidade ou cadastro são
              resolvidas diretamente pela sua academi. Use o chat ou detalhes da
              academia no aplicativo para mais informações.
            </CardText>
          </Card>
          <Card>
            <CardLabel>Privacidade</CardLabel>
            <CardTitle>Seus dados e sua conta</CardTitle>
            <CardText>
              Para excluir sua conta ou solicitar seus dados pessoais, use a{' '}
              <Link to="/delete-account">página de exclusão de conta</Link> ou
              diretamente pelo app.
            </CardText>
          </Card>
        </CardsGrid>

        <Section>
          <SectionTitle>Primeiros passos no aplicativo</SectionTitle>
          <SectionSubtitle>
            O primeiro passo muda conforme o seu tipo de acesso.
          </SectionSubtitle>

          <Track>
            <TrackTitle>Acesso vinculado a uma academia</TrackTitle>
            <StepsGrid>
              <Step>
                <StepNumber>01</StepNumber>
                <StepTitle>Baixe o app e insira o código recebido</StepTitle>
                <StepText>
                  O pré-cadastro é feito pela academia. Baixe o TrenzoX, informe
                  o código que você recebeu e complete o cadastro. Sua conta já
                  é criada vinculada à academia.
                </StepText>
              </Step>
              <Step>
                <StepNumber>02</StepNumber>
                <StepTitle>Acesse sua ficha de treino</StepTitle>
                <StepText>
                  A ficha prescrita pelo instrutor aparece na tela inicial, com
                  séries, repetições, cargas e demonstração de execução.
                </StepText>
              </Step>
              <Step>
                <StepNumber>03</StepNumber>
                <StepTitle>Registre e acompanhe</StepTitle>
                <StepText>
                  Marque as séries concluídas durante o treino para alimentar
                  seu histórico, evolução de cargas e insígnias.
                </StepText>
              </Step>
            </StepsGrid>
          </Track>

          <Track>
            <TrackTitle>Acesso avulso</TrackTitle>
            <StepsGrid>
              <Step>
                <StepNumber>01</StepNumber>
                <StepTitle>Baixe o app e faça o cadastro</StepTitle>
                <StepText>
                  Sem vínculo com nenhuma academia — instale o TrenzoX e crie
                  sua conta diretamente pelo aplicativo.
                </StepText>
              </Step>
              <Step>
                <StepNumber>02</StepNumber>
                <StepTitle>Crie suas fichas de treino</StepTitle>
                <StepText>
                  Monte seus próprios treinos, escolhendo exercícios, séries,
                  repetições e descanso.
                </StepText>
              </Step>
              <Step>
                <StepNumber>03</StepNumber>
                <StepTitle>Registre e acompanhe</StepTitle>
                <StepText>
                  Marque as séries concluídas durante o treino para alimentar
                  seu histórico e evolução de cargas.
                </StepText>
              </Step>
            </StepsGrid>
          </Track>

          <Track>
            <TrackTitle>
              Gestor — cadastrando a academia pelo painel web
            </TrackTitle>
            <StepsGrid>
              <Step>
                <StepNumber>01</StepNumber>
                <StepTitle>Cadastre sua academia</StepTitle>
                <StepText>
                  Faça o cadastro da academia e responsável inicial no painel
                  web para ter acesso ao dashboard de gestão.
                </StepText>
              </Step>
              <Step>
                <StepNumber>02</StepNumber>
                <StepTitle>Convide alunos e equipe</StepTitle>
                <StepText>
                  No dashboard, convide alunos e instrutores realizando o pré
                  cadastro e definindo os níveis de acesso. Cada usuário
                  receberá um e-mail com instruções para concluir o cadastro.
                </StepText>
              </Step>
              <Step>
                <StepNumber>03</StepNumber>
                <StepTitle>Monte fichas e acompanhe</StepTitle>
                <StepText>
                  Monte a ficha de treino de cada aluno pelo catálogo de
                  exercícios e acompanhe frequência, evolução e todas as
                  ferramentas do painel web.
                </StepText>
              </Step>
            </StepsGrid>
          </Track>
        </Section>

        <Section id="duvidas">
          <SectionTitle style={{ marginBottom: 36 }}>
            Dúvidas frequentes de suporte
          </SectionTitle>

          <FAQList>
            {faqs.map(faq => (
              <FAQItem key={faq.q}>
                <FAQQuestion>
                  <span>{faq.q}</span>
                  <span>+</span>
                </FAQQuestion>
                <FAQAnswer>{faq.a}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQList>
        </Section>

        <Section id="contato">
          <Panel>
            <SectionTitle style={{ fontSize: 28 }}>
              Abrir um chamado de suporte
            </SectionTitle>
            <SectionSubtitle style={{ margin: '10px 0 28px' }}>
              Quanto mais detalhes você enviar, mais rápido resolvemos.
              Respondemos por e-mail em até 1 dia útil.
            </SectionSubtitle>

            {sent ? (
              <SuccessBox>
                <div>Chamado recebido</div>
                <p>
                  Enviamos um e-mail de confirmação pro endereço informado.
                  Vamos responder em breve.
                </p>
              </SuccessBox>
            ) : (
              <Form onSubmit={handleSubmit}>
                <FieldsGrid>
                  <Field>
                    Nome completo
                    <input
                      name="full_name"
                      type="text"
                      required
                      placeholder="Seu nome"
                    />
                  </Field>
                  <Field>
                    E-mail da conta
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="voce@email.com"
                    />
                  </Field>
                  <Field>
                    Tipo de solicitação
                    <select
                      name="request_type"
                      required
                      defaultValue="technical_issue"
                    >
                      <option value="technical_issue">
                        Problema técnico no aplicativo
                      </option>
                      <option value="access">Acesso, login ou senha</option>
                      <option value="billing">
                        Dúvida sobre planos e cobrança
                      </option>
                      <option value="suggestion">Sugestão de melhoria</option>
                      <option value="other">Outro assunto</option>
                    </select>
                  </Field>
                  <Field>
                    Aparelho e versão do sistema (opcional)
                    <input
                      name="device_info"
                      type="text"
                      placeholder="Ex: iPhone 13 · iOS 18.2"
                    />
                  </Field>
                </FieldsGrid>

                <Field>
                  Descreva o que aconteceu
                  <textarea
                    name="message"
                    rows={5}
                    required
                    placeholder="Conte o passo a passo até o problema aparecer e qual mensagem foi exibida"
                  />
                </Field>

                {error && (
                  <ErrorText>
                    Não foi possível enviar seu chamado agora. Tente novamente
                    em instantes ou escreva direto para {branding.supportEmail}.
                  </ErrorText>
                )}

                <FormFooter>
                  <SubmitButton type="submit" disabled={sending}>
                    {sending ? 'Enviando...' : 'Enviar chamado'}
                  </SubmitButton>
                  <FooterNote>
                    Ou escreva para{' '}
                    <a href={`mailto:${branding.supportEmail}`}>
                      {branding.supportEmail}
                    </a>
                  </FooterNote>
                </FormFooter>
              </Form>
            )}

            <WarningCallout>
              <WarningTitle>Aviso importante</WarningTitle>
              <WarningText>
                O TrenzoX é uma ferramenta de organização e acompanhamento de
                treinos. Ele não substitui a orientação de um profissional de
                educação física nem avaliação médica. Consulte seu instrutor
                antes de alterar cargas ou a rotina de exercícios.
              </WarningText>
            </WarningCallout>
          </Panel>
        </Section>
      </Container>
      <Footer />
    </>
  );
};

export { Support };
